/**
 * カード印刷で使える「埋め込み可能な日本語フォント」のレジストリ (card-print)。
 *
 * PDF とプレビューで **同一フォント** を使い、見た目 (字幅・折り返し・太字) を一致させるための
 * 単一の真実の源。フォント選択ドロップダウン・@font-face・jsPDF 埋め込みはすべてここを参照する。
 *
 * ★ jsPDF 埋め込みの制約 (重要):
 *   jsPDF の font embed は glyf/loca テーブルベースの TrueType (sfnt=0x00010000) のみ対応。
 *   CFF (OTTO) の OpenType は埋め込めず壊れた PDF になる。また jsPDF は可変フォントの軸を駆動
 *   できず **既定マスター (Regular) のみ** 埋め込むため、本物の太字には **静的 Bold の TTF が別途必要**。
 *
 *   そのため、ここで参照する全フォントは glyf TrueType であることを検証済み、かつ太字対応の書体は
 *   静的 Regular + Bold を持つものを採用した:
 *     - Zen Kaku Gothic New (静的, R 2.3MB + B 2.2MB)  ofl/zenkakugothicnew/  本物の太字 ✓
 *     - BIZ UDPGothic       (静的, R 4.5MB + B 4.4MB)  ofl/bizudpgothic/      本物の太字 ✓
 *     - しっぽり明朝         (静的, R 8.3MB + B 8.2MB)  ofl/shipporimincho/    本物の太字 ✓
 *     - M PLUS 1 Code       (可変, Regular のみ)        ofl/mplus1code/        太字は擬似 (boldUrl なし)
 *
 *   Noto Sans JP は Google Fonts に静的 Bold が無く (可変のみ) 本物の太字を埋め込めないため不採用。
 *
 * フォントは git に同梱せず google/fonts を jsDelivr 経由・commit pin で参照する。
 * ユーザーごとに異なるシステムフォントは使わない (埋め込めるフォントのみ = WYSIWYG を保証)。
 */

export type CardFontId = 'zenkaku' | 'bizudp' | 'mincho' | 'mono';

export interface CardFontDef {
  id: CardFontId;
  /** UI ドロップダウン表示名。 */
  label: string;
  /** Regular の glyf TrueType URL (jsPDF 埋め込み + preview @font-face で共用)。 */
  url: string;
  /** Bold (700) の glyf TrueType URL。未指定なら太字は擬似 (輪郭線) で表現。 */
  boldUrl?: string;
  /** jsPDF に addFont する family 名。 */
  pdfFamily: string;
  /** preview の @font-face family 名 (system フォントと衝突しない専用名)。 */
  cssFamily: string;
  /** preview の総称フォールバック (フォント読込前/失敗時)。 */
  cssFallback: 'sans-serif' | 'serif' | 'monospace';
  /** OFL.txt の著作権表記。ライセンス表示・PDF メタデータに使う。 */
  copyright: string;
}

/** 全フォント共通のライセンス名 (全て SIL OFL 1.1)。 */
export const CARD_FONT_LICENSE = 'SIL Open Font License 1.1';
/** ライセンス全文・FAQ の参照先。 */
export const CARD_FONT_LICENSE_URL = 'https://openfontlicense.org/';

// google/fonts の commit pin (path/内容固定)。
const COMMIT = '295d98a7a0c17c68f1341eaeea354e7960ea70d3';
const GH = `https://cdn.jsdelivr.net/gh/google/fonts@${COMMIT}`;

export const CARD_FONTS: Record<CardFontId, CardFontDef> = {
  zenkaku: {
    id: 'zenkaku',
    label: 'ゴシック体 (Zen角ゴシック)',
    url: `${GH}/ofl/zenkakugothicnew/ZenKakuGothicNew-Regular.ttf`,
    boldUrl: `${GH}/ofl/zenkakugothicnew/ZenKakuGothicNew-Bold.ttf`,
    pdfFamily: 'ZenKakuGothicNew',
    cssFamily: 'ZenKakuGothicNewCard',
    cssFallback: 'sans-serif',
    copyright:
      'Copyright 2022 The Zen Kaku Gothic Project Authors (https://github.com/googlefonts/zen-kakugothic)',
  },
  bizudp: {
    id: 'bizudp',
    label: 'UDゴシック (BIZ UDP)',
    url: `${GH}/ofl/bizudpgothic/BIZUDPGothic-Regular.ttf`,
    boldUrl: `${GH}/ofl/bizudpgothic/BIZUDPGothic-Bold.ttf`,
    pdfFamily: 'BIZUDPGothic',
    cssFamily: 'BIZUDPGothicCard',
    cssFallback: 'sans-serif',
    copyright:
      'Copyright 2022 The BIZ UDGothic Project Authors (https://github.com/googlefonts/morisawa-biz-ud-gothic)',
  },
  mincho: {
    id: 'mincho',
    label: '明朝体 (しっぽり明朝)',
    url: `${GH}/ofl/shipporimincho/ShipporiMincho-Regular.ttf`,
    boldUrl: `${GH}/ofl/shipporimincho/ShipporiMincho-Bold.ttf`,
    pdfFamily: 'ShipporiMincho',
    cssFamily: 'ShipporiMinchoCard',
    cssFallback: 'serif',
    copyright:
      'Copyright 2021 The Shippori Mincho Project Authors (https://github.com/fontdasu/ShipporiMincho)',
  },
  mono: {
    id: 'mono',
    label: '等幅 (M PLUS 1 Code)',
    url: `${GH}/ofl/mplus1code/MPLUS1Code%5Bwght%5D.ttf`,
    // 静的 Bold が無いため boldUrl なし → 太字は輪郭線で擬似表現。
    pdfFamily: 'MPLUS1Code',
    cssFamily: 'MPLUS1CodeCard',
    cssFallback: 'monospace',
    copyright: 'Copyright 2021 The M+ FONTS Project Authors (https://github.com/coz-m/MPLUS_FONTS)',
  },
};

export const CARD_FONT_LIST: CardFontDef[] = [
  CARD_FONTS.zenkaku,
  CARD_FONTS.bizudp,
  CARD_FONTS.mincho,
  CARD_FONTS.mono,
];

export const DEFAULT_CARD_FONT_ID: CardFontId = 'zenkaku';

const VALID_IDS = new Set<CardFontId>(['zenkaku', 'bizudp', 'mincho', 'mono']);

/** 旧 theme.fontFamily (旧 id / CSS 文字列) → 現行 font id の対応 (後方互換)。 */
const LEGACY_TO_ID: Record<string, CardFontId> = {
  // 旧 font id (前バージョン: sans/serif/mono)
  sans: 'zenkaku',
  serif: 'mincho',
  // 旧 CSS 文字列
  'system-ui, -apple-system, sans-serif': 'zenkaku',
  '"noto sans jp", sans-serif': 'zenkaku',
  '"noto serif jp", serif': 'mincho',
  monospace: 'mono',
};

/**
 * 任意の theme.fontFamily 値を現行 font id に正規化する。
 * - 現行 id ('zenkaku'/'bizudp'/'mincho'/'mono') はそのまま
 * - 旧 id ('sans'→zenkaku, 'serif'→mincho) / 旧 CSS 文字列を対応表で吸収
 * - それ以外はヒューリスティック (UD系→bizudp、明朝/serif→mincho、mono/code→mono、既定 zenkaku)
 */
export function resolveCardFontId(value: unknown): CardFontId {
  if (typeof value !== 'string') return DEFAULT_CARD_FONT_ID;
  const v = value.trim() as CardFontId;
  if (VALID_IDS.has(v)) return v;
  const lower = value.trim().toLowerCase();
  if (LEGACY_TO_ID[lower]) return LEGACY_TO_ID[lower];
  if (/ud|biz/.test(lower)) return 'bizudp';
  if (/mincho|serif|明朝/.test(lower)) return 'mincho';
  if (/mono|code|等幅/.test(lower)) return 'mono';
  return DEFAULT_CARD_FONT_ID;
}

/** theme.fontFamily 値 → CardFontDef。 */
export function resolveCardFont(value: unknown): CardFontDef {
  return CARD_FONTS[resolveCardFontId(value)];
}

/** font id が本物の太字 (静的 Bold) を埋め込めるか。 */
export function hasEmbeddableBold(value: unknown): boolean {
  return !!resolveCardFont(value).boldUrl;
}

/**
 * 全フォントの @font-face CSS (preview 用)。
 * Regular(400) を宣言し、boldUrl があれば Bold(700) も宣言 (本物の太字)。
 * boldUrl が無いフォント (等幅) は 400 のみ宣言し、太字は描画側で -webkit-text-stroke。
 */
export function buildCardFontFaceCss(): string {
  const rules: string[] = [];
  for (const f of CARD_FONT_LIST) {
    rules.push(
      `@font-face{font-family:'${f.cssFamily}';src:url('${f.url}') format('truetype');font-weight:400;font-style:normal;font-display:swap;}`,
    );
    if (f.boldUrl) {
      rules.push(
        `@font-face{font-family:'${f.cssFamily}';src:url('${f.boldUrl}') format('truetype');font-weight:700;font-style:normal;font-display:swap;}`,
      );
    }
  }
  return rules.join('\n');
}

/** preview のカード文字に使う font-family 値 ('cssFamily', fallback)。 */
export function cardFontCss(value: unknown): string {
  const f = resolveCardFont(value);
  return `'${f.cssFamily}', ${f.cssFallback}`;
}
